import { useCallback, useEffect, useRef } from 'react';
import { logger } from '@gov-nx/api';
import { useAuthStoreUser } from '@gov-nx/store';
import { useBoolean } from '@gov-nx/utils/common/Hooks/useBoolean';
import { throttle } from '@gov-nx/utils/common/Time/delay';
import { useBroadcastChannel } from './useBroadcastChannel';

interface IEventRegister {
	addEventListener: (key: string, callback: () => void) => void;
	removeEventListener: (key: string, callback: () => void) => void;
}

const ACTIVITY_EVENTS: ReadonlyArray<keyof WindowEventMap> = [
	'mousemove',
	'mousedown',
	'touchstart',
	'touchmove',
	'click',
	'keydown',
	'scroll',
];

type ActivityMessage = { timestamp: number };

const CHANNEL_NAME = 'user-activity';

export const useUserInactivity = (
	config: {
		inactiveTimeout: number;
		autoLogoutTimeout: number;
		onInactive: () => void;
		onLogout: () => void;
	},
	eventRegister?: IEventRegister
) => {
	const { isLoggedIn } = useAuthStoreUser();
	const isActive = useBoolean(false);
	const inActiveTimerId = useRef<ReturnType<typeof setTimeout>>();
	const logoutTimerId = useRef<ReturnType<typeof setTimeout>>();

	const { postMessage } = useBroadcastChannel<ActivityMessage>(
		CHANNEL_NAME,
		({ timestamp }) => {
			if (timestamp > Date.now() - config.inactiveTimeout) {
				resetTimers();
			}
		}
	);

	const startTimers = useCallback(() => {
		if (isLoggedIn) {
			inActiveTimerId.current = setTimeout(() => {
				config.onInactive();
			}, config.inactiveTimeout);
			logoutTimerId.current = setTimeout(() => {
				logger('logoutTimerId.current -> onLogout', []);
				config.onLogout();
			}, config.autoLogoutTimeout);
		}
	}, [config, isLoggedIn]);

	const cancelTimers = useCallback(() => {
		if (inActiveTimerId.current) {
			clearTimeout(inActiveTimerId.current);
		}
		if (logoutTimerId.current) {
			clearTimeout(logoutTimerId.current);
		}
	}, []);

	const resetTimers = useCallback(() => {
		cancelTimers();
		startTimers();
	}, [cancelTimers, startTimers]);

	useEffect(() => {
		startTimers();

		return () => {
			cancelTimers();
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!isActive.value) return;

		const handleActivity = throttle(() => {
			const now = Date.now();
			postMessage({ timestamp: now });
			resetTimers();
		}, 1000);

		ACTIVITY_EVENTS.forEach((event) => {
			eventRegister?.addEventListener?.(event, handleActivity);
		});

		return () => {
			ACTIVITY_EVENTS.forEach((event) => {
				eventRegister?.removeEventListener?.(event, handleActivity);
			});
		};
	}, [eventRegister, isActive.value, postMessage, resetTimers]);

	return { start: isActive.setTrue };
};
